import { action, observable } from 'mobx';
import api from 'core/util/api';

/**
 * WebAuthn challenges expire on the frontend after 3 minutes.
 *
 * On the backend, WebAuthn challenges live for 5 minutes. We expire them on the frontend 2 minutes early. This ensures that the
 * user does not verify using a challenge which is technically still valid but close to expiration (as this would result
 * in confusing/surprising errors)
 */
const CHALLENGE_EXPIRY_MS = 3 * 60 * 1000;

/**
 * @typedef WebauthnChallengeStoreType
 * @property {() => Promise<import('@simplewebauthn/browser').PublicKeyCredentialRequestOptionsJSON>} getPromise
 * @property {() => void} expire
 * @property {boolean} loading
 * @property {() => function[]} setFetching use this to override the standard webAuthn challenge flow
 */

/**
 * @constructor
 * @class
 * @implements {WebauthnChallengeStoreType}
 */
class WebauthnChallengeStore {
  /**
   * Whether the challenge is currently loading
   * @type {boolean}
   */
  @observable
  loading = false;

  /**
   * The actual WebAuthn challenge object received from the backend.
   * Null if no challenge is currently available or if it has expired.
   *
   * Access via the return of this.getPromise()
   * @private
   */
  @observable.ref
  _challenge = null;

  /**
   * Holds the promise for the *currently ongoing* fetch operation.
   * This is used internally to avoid multiple fetches.
   *
   * Access via this.getPromise()
   * @private
   */
  _challengePromise = null;

  /**
   * Timer ID for the challenge expiry timeout.
   * @private
   */
  _expiryTimer = null;

  /**
   * @returns {Promise<import('@simplewebauthn/browser').PublicKeyCredentialRequestOptionsJSON>}
   */
  @action
  getPromise = () => {
    if (!this._challengePromise) {
      return this._fetchChallenge();
    }

    return this._challengePromise;
  };

  /**
   * Manually expires the currently stored challenge, forcing a new fetch
   * the next time `challengePromise` is accessed.
   * Also clears any pending expiry timer.
   * @param {boolean} [_force] only for internal usage
   */
  @action
  expire = (_force) => {
    if (this._expiryTimer) {
      clearTimeout(this._expiryTimer);
      this._expiryTimer = null;
    }
    // we want to avoid deleting an ongoing require to get new challenge
    if (!this.loading || _force) {
      this._challenge = null;
      this._challengePromise = null;
      this.loading = false;
    }
  };

  /**
   * Take control of this store so that you can set a challenge without firing off unnecessary fetches and triggering
   * a race condition. Only for use when you *know* that no request has been fired off yet i.e. as part of initial
   * authentication prior to displaying website content (/login, /whoami).
   *
   * @returns {[(challenge: import('@simplewebauthn/browser').PublicKeyCredentialRequestOptionsJSON) => void, (error?: Error) => void]}
   */
  @action
  setFetching = () => {
    if (this._challengePromise) {
      // probably this should be unreachable as we would always check `/whoami` or `/login` *before* any possible use
      // of this class. but be very careful if you start using other APIs to configure the challenge!
      console.error(
        'Race condition detected when creating WebAuthn challenge! This may result in authentication failures.'
      );
    }
    this.loading = true;
    let res;
    let rej;
    this._challengePromise = new Promise((resolve, reject) => {
      res = resolve;
      rej = reject;
    }).then(
      (result) => {
        if (result) {
          this._setChallenge(result);
        } else {
          this.expire(true);
        }
        return result;
      },
      (error) => {
        this._challengePromise = null;
        throw error;
      }
    );

    return [res, rej];
  };

  /**
   * Set the challenge results
   * @param {import('@simplewebauthn/browser').PublicKeyCredentialRequestOptionsJSON} challenge The challenge object from the backend.
   * @private
   */
  @action
  _setChallenge(challenge) {
    // Clear previous expiry timer if any
    if (this._expiryTimer) {
      clearTimeout(this._expiryTimer);
    }

    this._challenge = challenge;
    this.loading = false;

    // Set the expiry timer for the new challenge
    this._expiryTimer = setTimeout(() => {
      // Use expire action to ensure proper cleanup
      this.expire();
    }, CHALLENGE_EXPIRY_MS);
  }

  /**
   * Fetches a new WebAuthn challenge from the backend.
   * Avoids fetching if a request is already in progress.
   * Returns the promise associated with the fetch operation.
   * This is the core function that interacts with the API.
   *
   * @returns {Promise<import('@simplewebauthn/browser').PublicKeyCredentialRequestOptionsJSON>}
   * @private
   */
  @action
  _fetchChallenge() {
    // Don't fetch if already fetching, instead return the existing promise.
    if (this._challengePromise) {
      return this._challengePromise;
    }

    if (this._challenge) {
      // If there was no previous request for a challenge but it's already present in store then we send it out.
      // This should not actually happen as `setFetching` should have been called.
      this._challengePromise = Promise.resolve(this._challenge);
      // ensure loading state is set correctly (it should already be set but we do it just in case)
      this.loading = false;
    } else {
      this.loading = true;

      // Otherwise we start a new request
      this._challengePromise = api
        .post('/api/ui/2fa/challenge')
        .then((challenge) => {
          this._setChallenge(challenge);
          return challenge;
        })
        .catch((error) => {
          console.error('FetchChallenge: API call failed:', error);
          this.expire(true);
          throw error;
        });
    }

    return this._challengePromise;
  }
}

/**
 * @type {WebauthnChallengeStoreType}
 */
export const webauthnChallenge = new WebauthnChallengeStore();
